/**
 * StreamingDashboardHeader
 *
 * qb.web-style header for the Streaming Dashboard.
 * Displays title, subtitle, status chip, library counts, and Reconnect action.
 * Flyout panels (Analytics, Logs, Settings) are triggered exclusively from the
 * sidebar navigation children -- no duplicate buttons here.
 *
 * Supports a 2-column layout: left column for title/meta, right column for
 * the GPU telemetry reveal panel when expanded.
 */

import type { ReactNode } from "react";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { GpuTelemetrySection } from "./GpuTelemetrySection";
import type { GpuTimeseriesRange } from "./GpuTelemetrySection";
import type { GpuSample, GpuStreamStatus } from "../../hooks/useIntelGpuStream";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ConnectionStatus = "connecting" | "connected" | "reconnecting" | "error" | "disconnected" | "disabled";

/** Library counts for display */
type LibraryCounts = {
  movieCount: number;
  seriesCount: number;
  episodeCount: number;
} | null;

type GpuProps = {
  expanded: boolean;
  range: GpuTimeseriesRange;
  onRangeChange: (range: GpuTimeseriesRange) => void;
  mode: "live" | "paused";
  onToggleMode: () => void;
  latest: GpuSample | null;
  history: GpuSample[];
  status: GpuStreamStatus;
};

type StreamingDashboardHeaderProps = {
  /** Page title (default: "Streaming Dashboard") */
  title?: string;
  /** Subtitle text or node */
  subtitle?: ReactNode;
  /** SSE connection status */
  connectionStatus: ConnectionStatus;
  /** Number of active sessions */
  sessionCount: number;
  /** Library totals (Movies, Series, Episodes) */
  libraryCounts?: LibraryCounts;
  /** Reconnect to SSE stream */
  onReconnect: () => void;
  /** Whether a media server is configured */
  serviceConfigured: boolean;
  /** Whether any media server is degraded or unreachable (stale data shown) */
  isServerDegraded?: boolean;
  /** Additional actions rendered in the header toolbar */
  actions?: ReactNode;
  /** GPU telemetry props for the right-column reveal */
  gpu?: GpuProps;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const statusConfig: Record<ConnectionStatus, { label: string; variant: "success" | "warning" | "danger" | "muted" }> = {
  connecting: { label: "Connecting", variant: "muted" },
  connected: { label: "Live", variant: "success" },
  reconnecting: { label: "Reconnecting", variant: "warning" },
  error: { label: "Error", variant: "danger" },
  disconnected: { label: "Disconnected", variant: "danger" },
  disabled: { label: "Disabled", variant: "muted" },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Format a number with locale-aware separators */
const formatCount = (n: number): string => n.toLocaleString();

export const StreamingDashboardHeader = ({
  title = "Streaming Dashboard",
  subtitle,
  connectionStatus,
  sessionCount,
  libraryCounts,
  onReconnect,
  serviceConfigured,
  isServerDegraded = false,
  actions,
  gpu,
}: StreamingDashboardHeaderProps) => {
  const status = statusConfig[connectionStatus];
  const showReconnect = (connectionStatus === "error" || connectionStatus === "disconnected") && serviceConfigured;

  // Build default subtitle if not provided
  const defaultSubtitle = !serviceConfigured
    ? "Configure media servers to monitor active streams"
    : `Real-time monitoring of ${sessionCount} active ${sessionCount === 1 ? "stream" : "streams"}`;

  return (
    <div className="mb-6">
      {/* 2-column grid: left = title/meta, right = GPU reveal (when expanded) */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,1fr)] gap-4">
        {/* Left column: Title + status + reconnect */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              {/* Accent bar - qb.web style gradient (theme-aware via CSS variable) */}
              <div
                className="hidden sm:block w-1 h-8 rounded-full"
                style={{ background: "var(--header-gradient)" }}
              />
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                {title}
              </h1>
              {serviceConfigured && (
                <Badge variant={status.variant} size="sm" className="ml-2">
                  {status.label}
                </Badge>
              )}
              {serviceConfigured && isServerDegraded && connectionStatus === "connected" && (
                <Badge
                  variant="warning"
                  size="sm"
                  className="ml-1 gap-1"
                  title="Media server is slow to respond. Showing last known sessions."
                >
                  <Icon icon="lucide:clock" className="h-3 w-3" aria-hidden />
                  Server Slow
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground max-w-xl">
              {subtitle ?? defaultSubtitle}
            </p>
            {serviceConfigured && libraryCounts && (
              <p className="mt-1 text-xs text-muted-foreground">
                <span className="font-medium">{formatCount(libraryCounts.movieCount)}</span> Movies
                {" · "}
                <span className="font-medium">{formatCount(libraryCounts.seriesCount)}</span> Series
                {" · "}
                <span className="font-medium">{formatCount(libraryCounts.episodeCount)}</span> Episodes
              </p>
            )}
          </div>

          {/* Header toolbar: actions + reconnect */}
          <div className="flex items-center gap-2">
            {actions}
            {showReconnect && (
              <Button variant="secondary" size="sm" onClick={onReconnect} className="gap-1.5">
                <Icon icon="lucide:refresh-cw" className="h-3.5 w-3.5" aria-hidden />
                Reconnect
              </Button>
            )}
          </div>
        </div>

        {/* Right column: GPU telemetry reveal */}
        {gpu && (
          <GpuTelemetrySection
            expanded={gpu.expanded}
            range={gpu.range}
            onRangeChange={gpu.onRangeChange}
            mode={gpu.mode}
            onToggleMode={gpu.onToggleMode}
            latest={gpu.latest}
            history={gpu.history}
            status={gpu.status}
          />
        )}
      </div>
    </div>
  );
};
